import { useState, useEffect, useRef, useMemo } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { ImageUploader } from '../common/ImageUploader';
import { GOOGLE_MAPS_API_KEY, mapLibraries } from '../../lib/map/constants';
import { saveAddress } from '../../lib/map/addressFormUtils';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PhoneOff, Phone, HelpCircle } from 'lucide-react';

// Define address label types
type AddressLabelType = 'HOME' | 'OFFICE' | 'SHOP' | 'OTHER';
// Define service language types
type ServiceLanguageType = 'EN' | 'TH';
// Define call preference types
type CallPreferenceType = 'ALWAYS' | 'NEVER' | 'NO_PREFERENCE';

interface AddressFormProps {
  markerPosition: { lat: number; lng: number };
  editMode?: boolean;
  addressToEdit?: any;
  userPhone?: string;
  onBack: () => void;
  onSave: (address: any) => void;
  initialValues?: {
    addressName?: string;
    addressDetails?: string;
    contactName?: string;
    contactPhone?: string;
    deliveryNote?: string;
    images?: string[];
    addressLabel?: AddressLabelType;
    serviceLanguage?: ServiceLanguageType;
    callPreference?: CallPreferenceType;
  };
}

export default function AddressForm({
  markerPosition,
  editMode = false,
  addressToEdit,
  userPhone,
  onBack,
  onSave,
  initialValues
}: AddressFormProps) {
  const { t } = useTranslation();
  
  // Form state
  const [addressName, setAddressName] = useState<string>(initialValues?.addressName || '');
  const [addressDetails, setAddressDetails] = useState<string>(initialValues?.addressDetails || '');
  const [contactName, setContactName] = useState<string>(initialValues?.contactName || '');
  const [contactPhone, setContactPhone] = useState<string>(initialValues?.contactPhone || '');
  const [deliveryNote, setDeliveryNote] = useState<string>(initialValues?.deliveryNote || '');
  const [images, setImages] = useState<string[]>(initialValues?.images || []);
  const [addressLabel, setAddressLabel] = useState<AddressLabelType>(initialValues?.addressLabel || 'HOME');
  const [serviceLanguage, setServiceLanguage] = useState<ServiceLanguageType>(initialValues?.serviceLanguage || 'EN');
  const [callPreference, setCallPreference] = useState<CallPreferenceType>(initialValues?.callPreference || 'NO_PREFERENCE');
  const [formInitialized, setFormInitialized] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  
  // Add state to track the actual map position (may differ from markerPosition prop)
  const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number }>(markerPosition);
  
  // Add a reference to track the last valid images received from ImageUploader
  const lastValidImagesRef = useRef<string[]>([]);
  
  // Add a ref to track if we've already set coordinates from addressToEdit
  const addressCoordsSetRef = useRef<boolean>(false);
  
  // Initialize Google Maps API loader for map preview
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: mapLibraries,
  });

  // Enhance safeSetImages to maintain the reference of known good images
  const safeSetImages = (imageUrls: string[]) => {
    // Filter out any blob URLs to prevent errors with expired blobs
    const filteredImages = imageUrls.filter(img => !img.startsWith('blob:'));
    
    if (filteredImages.length !== imageUrls.length) {
      console.log('[AddressForm] Filtered out', imageUrls.length - filteredImages.length, 'blob URLs from images');
      
      // If we're left with no images but had valid ones before, keep the last known good images
      if (filteredImages.length === 0 && lastValidImagesRef.current.length > 0) {
        console.log('[AddressForm] Using last known valid images instead of empty array');
        setImages(lastValidImagesRef.current);
        return;
      }
    }
    
    // If we have valid images, update our reference
    if (filteredImages.length > 0) {
      lastValidImagesRef.current = filteredImages;
    }
    
    setImages(filteredImages);
  };
  
  // Handle image change from the ImageUploader
  const handleImagesChange = (newImages: string[]) => {
    console.log('[AddressForm] Received new images from ImageUploader:', newImages);
    
    // Only process non-blob URLs
    const validImages = newImages.filter(img => !img.startsWith('blob:'));
    
    // Check if we actually have valid HTTP URLs (not blobs)
    if (validImages.length > 0) {
      console.log('[AddressForm] Accepted valid images:', validImages);
      lastValidImagesRef.current = validImages;
      setImages(validImages);
    } else if (newImages.length === 0) {
      // If the uploader is clearing all images, respect that
      console.log('[AddressForm] Clearing all images');
      lastValidImagesRef.current = [];
      setImages([]);
    } else {
      // Log but ignore blob URLs - don't update state
      console.log('[AddressForm] Ignoring update with only blob URLs');
    }
  };

  // Modify the initialize form effect to use the safe setter
  useEffect(() => {
    if (editMode && addressToEdit && !formInitialized) {
      console.log('[AddressForm] Initializing edit form with data:', addressToEdit);
      
      // Set values directly
      setAddressName(addressToEdit.name || addressToEdit.locationName || '');
      
      let detailsValue = addressToEdit.details || '';
      if (!detailsValue && addressToEdit.street) {
        detailsValue = `${addressToEdit.street || ''}`;
        if (addressToEdit.city) detailsValue += `, ${addressToEdit.city}`;
        if (addressToEdit.state) detailsValue += `, ${addressToEdit.state}`;
        if (addressToEdit.zipCode) detailsValue += ` ${addressToEdit.zipCode}`;
        if (addressToEdit.country) detailsValue += `, ${addressToEdit.country}`;
      }
      
      setAddressDetails(detailsValue);
      setContactName(addressToEdit.contactName || '');
      setContactPhone(addressToEdit.contactPhone || userPhone || '');
      setDeliveryNote(addressToEdit.deliveryNote || '');
      setAddressLabel(addressToEdit.addressLabel || 'HOME');
      setServiceLanguage(addressToEdit.serviceLanguage || 'EN');
      setCallPreference(addressToEdit.callPreference || 'NO_PREFERENCE');
      
      // Use the safe setter for images
      safeSetImages(addressToEdit.images || []);
      
      // Initialize map position from coordinates if available
      if (addressToEdit.latitude && addressToEdit.longitude) {
        try {
          const lat = parseFloat(addressToEdit.latitude);
          const lng = parseFloat(addressToEdit.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            console.log('[AddressForm] Setting map position from form initialization:', { lat, lng });
            setMapPosition({ lat, lng });
            // Mark that we've set coordinates from addressToEdit
            addressCoordsSetRef.current = true;
          }
        } catch (e) {
          console.error('[AddressForm] Error parsing coordinates:', e);
        }
      }
      
      // Set form initialized flag
      setFormInitialized(true);
    }
  }, [editMode, addressToEdit, formInitialized, userPhone]);
  
  // Set user's phone number if available and not in edit mode
  useEffect(() => {
    if (!editMode && userPhone && !contactPhone) {
      // Preserve original phone number format
      setContactPhone(userPhone);
    }
  }, [userPhone, editMode, contactPhone]);
  
  // Update map position when markerPosition prop changes
  useEffect(() => {
    // Only update from markerPosition when not in edit mode or when coordinates haven't been set from addressToEdit
    if (!editMode || !addressCoordsSetRef.current) {
      console.log('[AddressForm] Updating map position from markerPosition:', markerPosition);
      setMapPosition(markerPosition);
    } else {
      console.log('[AddressForm] Ignoring markerPosition update in edit mode with existing coordinates');
    }
  }, [markerPosition, editMode]);

  // Handle form submission
  const handleSaveAddress = async () => {
    // Final validation of images to ensure no blob URLs are included
    const validImages = images.filter(img => !img.startsWith('blob:'));
    
    // If we've filtered out everything but had valid images before, use the last known good ones
    if (validImages.length === 0 && lastValidImagesRef.current.length > 0) {
      console.log('[AddressForm] Using last known valid images instead of empty array for saving');
      validImages.push(...lastValidImagesRef.current);
    }
    
    if (validImages.length !== images.length) {
      console.log('[AddressForm] Filtered out blob URLs before saving:', images.length - validImages.length);
      // Update the UI with cleaned images
      safeSetImages(validImages);
    }
    
    // Create form data object
    const formData = {
      addressName,
      addressDetails,
      contactName,
      contactPhone,
      deliveryNote,
      addressLabel,
      serviceLanguage,
      callPreference,
      images: validImages // Use the validated images array
    };
    
    console.log('[AddressForm] Saving address with images:', validImages);
    
    // Use utility function to save or update the address
    const savedAddress = await saveAddress(
      formData,
      mapPosition, // Use the mapPosition (from editing) instead of markerPosition
      editMode,
      addressToEdit?.id
    );
    
    if (savedAddress) {
      // Call the parent's onSave function with the saved address
      onSave({
        address: addressDetails,
        locationName: addressName,
        latitude: mapPosition.lat,
        longitude: mapPosition.lng,
        addressLabel,
        serviceLanguage,
        callPreference,
        ...savedAddress // Pass full saved address object
      });
    }
  };

  return (
    <div className="flex flex-col h-full overflow-auto bg-white">
      {/* Map preview */}
      <div className={`mx-4 ${editMode ? 'h-48' : 'h-28'} bg-gray-200 rounded-lg overflow-hidden ${editMode ? 'my-4' : 'mt-3 mb-2'} relative`}>
        {isEditLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="ml-2 text-gray-600">{t('addressForm.loadingMap')}</p>
          </div>
        ) : isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={{ lat: mapPosition.lat, lng: mapPosition.lng }}
            zoom={15}
            options={{
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              zoomControl: false,
              draggable: false,
            }}
          >
            <Marker 
              position={{ lat: mapPosition.lat, lng: mapPosition.lng }}
            />
          </GoogleMap>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-gray-500">{t('addressForm.loadingMapPreview')}</p>
          </div>
        )}
        
        {/* Custom pin overlay - Only show when map is loaded and not loading */}
        {isLoaded && !isEditLoading && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
            <div className="w-6 h-6 bg-red-500 border-2 border-white rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        )}
        
        {/* Only show adjustment button when map is loaded and not loading */}
        {isLoaded && !isEditLoading && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center">
            <button 
              className={`bg-white py-1.5 px-3 rounded-full shadow-md ${editMode ? 'text-sm' : 'text-xs'}`}
              onClick={onBack}
            >
              {t('addressForm.adjustPin')}
            </button>
          </div>
        )}
      </div>

      {/* GPS Coordinates Display */}
      <div className={`mx-4 ${editMode ? '-mt-2 mb-3' : '-mt-1 mb-2'}`}>
        <p className={`${editMode ? 'text-xs' : 'text-[10px]'} text-gray-500 text-center`}>
          {t('addressForm.coordinates', { lat: mapPosition.lat.toFixed(6), lng: mapPosition.lng.toFixed(6) })}
        </p>
      </div>

      {/* Form */}
      <div className={`px-4 flex flex-col ${editMode ? 'gap-6' : 'gap-4'} flex-1 overflow-y-auto`}>
        {/* Address Name */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.addressName')} <b className="text-red-500">*</b>
          </label>
          <input
            type="text"
            value={addressName || ''}
            onChange={(e) => setAddressName(e.target.value)}
            className={`w-full ${editMode ? 'p-4' : 'p-3'} border border-gray-300 rounded-[16px] focus:outline-none ${editMode ? '' : 'text-sm'}`}
            placeholder={t('addressForm.enterAddressName')}
          />
        </div>

        {/* Contact Name */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.contactName')} <b className="text-red-500">*</b>
          </label>
          <input
            type="text"
            value={contactName || ''}
            onChange={(e) => setContactName(e.target.value)}
            className={`w-full ${editMode ? 'p-4' : 'p-3'} border border-gray-300 rounded-[16px] focus:outline-none ${editMode ? '' : 'text-sm'}`}
            placeholder={t('addressForm.enterContactName')}
          />
        </div>

        {/* Contact Phone */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.contactPhone')} <b className="text-red-500">*</b>
          </label>
          <input
            type="tel"
            value={contactPhone || ''}
            maxLength={10}
            onChange={(e) => {
              if(e.target.value.match(/^\d+$/) || e.target.value == '') {
                setContactPhone(e.target.value);
              }
            }}
            className={`w-full ${editMode ? 'p-4' : 'p-3'} border border-gray-300 rounded-[16px] focus:outline-none ${editMode ? '' : 'text-sm'}`}
            placeholder={t('addressForm.enterPhoneNumber')}
          />
        </div>

        {/* Details */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.details')} <b className="text-red-500">*</b>
          </label>
          <textarea
            value={addressDetails || ''}
            onChange={(e) => setAddressDetails(e.target.value)}
            className={`w-full ${editMode ? 'p-4' : 'p-3'} border border-gray-300 rounded-[16px] focus:outline-none ${editMode ? 'h-24' : 'h-20'} ${editMode ? '' : 'text-sm'}`}
            placeholder={t('addressForm.enterAddressDetails')}
          />
        </div>

        {/* Address Label */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.label')}
          </label>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setAddressLabel('HOME')}
              className={`flex items-center gap-2 ${editMode ? 'py-3 px-5' : 'py-2 px-4'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                addressLabel === 'HOME' 
                  ? 'border-green-500 bg-green-50 text-green-500' 
                  : 'border-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
              </svg>
              {t('addressForm.home')}
            </button>
            <button
              type="button"
              onClick={() => setAddressLabel('OFFICE')}
              className={`flex items-center gap-2 ${editMode ? 'py-3 px-5' : 'py-2 px-4'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                addressLabel === 'OFFICE' 
                  ? 'border-gray-700 bg-gray-100 text-gray-700' 
                  : 'border-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2a1 1 0 00-1-1H7a1 1 0 00-1 1v2a1 1 0 01-1 1H3a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
              {t('addressForm.office')}
            </button>
            <button
              type="button"
              onClick={() => setAddressLabel('SHOP')}
              className={`flex items-center gap-2 ${editMode ? 'py-3 px-5' : 'py-2 px-4'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                addressLabel === 'SHOP' 
                  ? 'border-blue-500 bg-blue-50 text-blue-500' 
                  : 'border-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} viewBox="0 0 20 20" fill="currentColor">
                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              {t('addressForm.shop')}
            </button>
            <button
              type="button"
              onClick={() => setAddressLabel('OTHER')}
              className={`flex items-center gap-2 ${editMode ? 'py-3 px-5' : 'py-2 px-4'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                addressLabel === 'OTHER' 
                  ? 'border-purple-500 bg-purple-50 text-purple-500' 
                  : 'border-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              {t('addressForm.other')}
            </button>
          </div>
        </div>

        {/* Service Language */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.serviceLanguage')}
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setServiceLanguage('EN')}
              className={`flex-1 ${editMode ? 'py-3' : 'py-2'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                serviceLanguage === 'EN' 
                  ? 'border-blue-500 bg-blue-50 text-blue-500' 
                  : 'border-gray-300'
              }`}
            >
              English
            </button>
            <button
              type="button"
              onClick={() => setServiceLanguage('TH')}
              className={`flex-1 ${editMode ? 'py-3' : 'py-2'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                serviceLanguage === 'TH' 
                  ? 'border-orange-500 bg-orange-50 text-orange-500' 
                  : 'border-gray-300'
              }`}
            >
              Thai
            </button>
          </div>
        </div>

        {/* Call Preference */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.callPreference')}
          </label>
          <div className="flex flex-col gap-3">
                         <button
               type="button"
               onClick={() => setCallPreference('ALWAYS')}
               className={`flex items-center gap-3 ${editMode ? 'py-3 px-4' : 'py-2 px-3'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                 callPreference === 'ALWAYS' 
                   ? 'border-green-500 bg-green-50 text-green-500' 
                   : 'border-gray-300'
               }`}
             >
               <Phone className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} />
               {t('addressForm.alwaysCall')}
             </button>
                         <button
               type="button"
               onClick={() => setCallPreference('NEVER')}
               className={`flex items-center gap-3 ${editMode ? 'py-3 px-4' : 'py-2 px-3'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                 callPreference === 'NEVER' 
                   ? 'border-red-500 bg-red-50 text-red-500' 
                   : 'border-gray-300'
               }`}
             >
               <PhoneOff className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} />
               {t('addressForm.neverCall')}
             </button>
                         <button
               type="button"
               onClick={() => setCallPreference('NO_PREFERENCE')}
               className={`flex items-center gap-3 ${editMode ? 'py-3 px-4' : 'py-2 px-3'} rounded-[16px] border ${editMode ? '' : 'text-sm'} ${
                 callPreference === 'NO_PREFERENCE' 
                   ? 'border-gray-500 bg-gray-50 text-gray-500' 
                   : 'border-gray-300'
               }`}
             >
               <HelpCircle className={`${editMode ? 'h-5 w-5' : 'h-4 w-4'}`} />
               {t('addressForm.noCallPreference')}
             </button>
          </div>
        </div>

        {/* Delivery Note */}
        <div>
          <label className={`block text-gray-700 ${editMode ? 'text-lg' : 'text-base'} font-medium mb-2`}>
            {t('addressForm.deliveryNote')}
          </label>
          <textarea
            value={deliveryNote || ''}
            onChange={(e) => setDeliveryNote(e.target.value)}
            className={`w-full ${editMode ? 'p-4' : 'p-3'} border border-gray-300 rounded-[16px] focus:outline-none ${editMode ? 'h-24' : 'h-20'} ${editMode ? '' : 'text-sm'}`}
            placeholder={t('addressForm.enterDeliveryInstructions')}
          />
        </div>

        {/* Images */}
        <ImageUploader
          images={images}
          onChange={handleImagesChange}
          isEditMode={editMode}
        />
      </div>

      {/* Save button */}
      <div className="p-4 mt-auto">
        <button 
          onClick={handleSaveAddress}
          className={`w-full ${editMode ? 'h-[65px]' : 'h-[56px]'} rounded-[16px] font-medium text-black bg-[#F9D348]`}
        >
          {editMode ? t('addressForm.updateAddress') : t('addressForm.saveAddress')}
        </button>
      </div>
    </div>
  );
} 